import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { API_URL } from './config';
import './Earnings.css';

const DAYS = [
  { key: 'MON', label: 'Monday' },
  { key: 'TUE', label: 'Tuesday' },
  { key: 'WED', label: 'Wednesday' },
  { key: 'THU', label: 'Thursday' },
  { key: 'FRI', label: 'Friday' },
];
const TIMINGS = [
  { key: 'BMO', label: 'Before Open' },
  { key: 'AMC', label: 'After Close' },
  { key: 'UNKNOWN', label: 'Unknown / Not Confirmed' },
];

const FILTER_OPTIONS = [
  { value: 'top100', label: 'Top 100 by market cap' },
  { value: 'all', label: 'All companies' },
  { value: '500', label: '$500M+' },
  { value: '1000', label: '$1B+' },
  { value: '2000', label: '$2B+' },
  { value: '5000', label: '$5B+' },
  { value: '10000', label: '$10B+' },
  { value: '25000', label: '$25B+' },
  { value: '50000', label: '$50B+' },
  { value: '100000', label: '$100B+' },
];

function formatWeekLabel(weekStartDate) {
  const start = new Date(weekStartDate + 'T00:00:00');
  const end = new Date(start);
  end.setDate(start.getDate() + 4);
  const fmt = (d) => d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
  return `${fmt(start)} – ${fmt(end)}, ${start.getFullYear()}`;
}

function formatFullDate(isoDate) {
  if (!isoDate) return 'Date unknown';
  const d = new Date(isoDate + 'T00:00:00');
  return d.toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' });
}

function toISODate(d) {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

/** Monday of the calendar week containing `d`, as YYYY-MM-DD. */
function mondayOf(d) {
  const day = d.getDay(); // 0=Sun..6=Sat
  const diff = (day === 0 ? -6 : 1) - day;
  const monday = new Date(d);
  monday.setDate(d.getDate() + diff);
  return toISODate(monday);
}

function addDays(isoDate, n) {
  const d = new Date(isoDate + 'T00:00:00');
  d.setDate(d.getDate() + n);
  return toISODate(d);
}

/** 'This week' / 'Past week' / 'Next week' relative to today, or null otherwise. */
function getWeekLabel(weekStartDate) {
  const thisWeek = mondayOf(new Date());
  if (weekStartDate === thisWeek) return 'This week';
  if (weekStartDate === addDays(thisWeek, -7)) return 'Past week';
  if (weekStartDate === addDays(thisWeek, 7)) return 'Next week';
  return null;
}

/** Which week to auto-select: next week from Friday through the weekend (the trading week
 * is done, so what's coming up is more relevant), otherwise this week. */
function getDefaultWeekStart() {
  const dow = new Date().getDay(); // 0=Sun..6=Sat
  const thisWeek = mondayOf(new Date());
  return (dow === 5 || dow === 6 || dow === 0) ? addDays(thisWeek, 7) : thisWeek;
}

/** Market cap is stored in millions USD; always displayed in billions per site convention
 * (e.g. 500 -> "0.5B"). */
function formatMarketCap(millions) {
  if (millions === null || millions === undefined) return null;
  const billions = millions / 1000;
  return `${billions.toLocaleString(undefined, { minimumFractionDigits: 1, maximumFractionDigits: 1 })}B`;
}

function Earnings() {
  const [weeks, setWeeks] = useState([]);
  const [selectedWeekId, setSelectedWeekId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [heldSymbols, setHeldSymbols] = useState(new Map());
  const [filterMode, setFilterMode] = useState(() => {
    const saved = localStorage.getItem('earnings_marketcap_filter');
    return FILTER_OPTIONS.some((opt) => opt.value === saved) ? saved : 'top100';
  });
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    localStorage.setItem('earnings_marketcap_filter', filterMode);
  }, [filterMode]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    axios.get(`${API_URL}/portfolio/earnings-weeks/`)
      .then((res) => {
        if (cancelled) return;
        const list = res.data.results || res.data;
        setWeeks(list);
        const target = getDefaultWeekStart();
        const targeted = list.find((w) => w.week_start_date === target);
        const thisWeek = list.find((w) => w.week_start_date === mondayOf(new Date()));
        setSelectedWeekId((targeted || thisWeek || list[0])?.id ?? null);
      })
      .catch((err) => {
        if (cancelled) return;
        setError(err.response?.data?.error || err.message || 'Failed to load earnings data');
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, []);

  // Tickers held across BOTH accounts (patrick + richard), with who holds each one —
  // always shown regardless of the market cap filter, and highlighted with the holder's
  // name, no matter who's currently logged in. Map: TICKER -> ['patrick', 'richard', ...]
  useEffect(() => {
    let cancelled = false;
    axios.get(`${API_URL}/portfolio/held-tickers/`)
      .then((res) => {
        if (cancelled) return;
        setHeldSymbols(new Map(Object.entries(res.data.tickers || {})));
      })
      .catch(() => { if (!cancelled) setHeldSymbols(new Map()); });
    return () => { cancelled = true; };
  }, []);

  const selectedWeek = useMemo(
    () => weeks.find((w) => w.id === selectedWeekId) || null,
    [weeks, selectedWeekId]
  );

  const heldInfo = (release) => heldSymbols.get((release.ticker || '').toUpperCase());
  // Only an *open* position bypasses the market cap filter — a fully-closed past trade
  // doesn't force a company onto the page, it's only styled differently if it's already
  // showing.
  const isOpenHeld = (release) => {
    const info = heldInfo(release);
    return !!info && Object.keys(info.open).length > 0;
  };
  const capitalize = (s) => s.charAt(0).toUpperCase() + s.slice(1);
  const ASSET_LABELS = { STK: 'Stock', OPT: 'Option', WAR: 'Warrant' };
  const BROKER_LABELS = { ibkr: 'IBKR', tastytrade: 'Tasty' };

  const isSearching = searchQuery.trim() !== '';

  const matchesSearch = (release) => {
    const query = searchQuery.trim().toLowerCase();
    if (!query) return true;
    return (
      (release.ticker || '').toLowerCase().includes(query) ||
      (release.company_name || '').toLowerCase().includes(query)
    );
  };

  // A typed search ignores the selected week entirely and looks across all known weeks —
  // the point is to find a company's next earnings date even if it's not in the week
  // currently on screen.
  const searchResults = useMemo(() => {
    if (!isSearching) return [];
    return weeks
      .flatMap((w) => w.releases.map((r) => ({ ...r, weekStartDate: w.week_start_date })))
      .filter(matchesSearch)
      .sort((a, b) => (a.release_date || '').localeCompare(b.release_date || ''));
  }, [weeks, searchQuery]);

  const totalReleaseCount = useMemo(
    () => weeks.reduce((sum, w) => sum + w.releases.length, 0),
    [weeks]
  );

  const filteredReleases = useMemo(() => {
    if (!selectedWeek) return [];
    const all = selectedWeek.releases;

    if (filterMode === 'all') return all;

    if (filterMode === 'top100') {
      const sorted = [...all].sort((a, b) => (b.market_cap_millions || 0) - (a.market_cap_millions || 0));
      const topIds = new Set(sorted.slice(0, 100).map((r) => r.id));
      return all.filter((r) => topIds.has(r.id) || isOpenHeld(r));
    }

    const threshold = Number(filterMode);
    return all.filter((r) => (r.market_cap_millions || 0) >= threshold || isOpenHeld(r));
  }, [selectedWeek, filterMode, heldSymbols]);

  const grouped = useMemo(() => {
    const map = {};
    for (const day of DAYS) {
      map[day.key] = { BMO: [], AMC: [], UNKNOWN: [] };
    }
    for (const r of filteredReleases) {
      if (map[r.day] && map[r.day][r.timing]) {
        map[r.day][r.timing].push(r);
      }
    }
    for (const day of DAYS) {
      for (const timing of TIMINGS) {
        map[day.key][timing.key].sort((a, b) => (b.market_cap_millions || 0) - (a.market_cap_millions || 0));
      }
    }
    return map;
  }, [filteredReleases]);

  if (loading) {
    return <div className="earnings-container"><p>Loading earnings calendar…</p></div>;
  }
  if (error) {
    return <div className="earnings-container"><p className="earnings-error">{error}</p></div>;
  }

  return (
    <div className="earnings-container">
      <aside className="earnings-week-sidebar">
        <h3>Weeks</h3>
        {weeks.length === 0 ? (
          <p className="earnings-empty-hint">No weeks yet.</p>
        ) : (
          <ul className="earnings-week-list">
            {weeks.map((w) => {
              const label = getWeekLabel(w.week_start_date);
              return (
                <li key={w.id}>
                  <button
                    className={`earnings-week-item ${w.id === selectedWeekId ? 'active' : ''}`}
                    onClick={() => setSelectedWeekId(w.id)}
                  >
                    <span className="earnings-week-range">{formatWeekLabel(w.week_start_date)}</span>
                    {w.quarter_label && <span className="earnings-week-quarter">{w.quarter_label}</span>}
                    {label && (
                      <span className={`earnings-week-badge earnings-week-badge-${label.split(' ')[0].toLowerCase()}`}>
                        {label}
                      </span>
                    )}
                  </button>
                </li>
              );
            })}
          </ul>
        )}
      </aside>

      <div className="earnings-main">
        {!selectedWeek ? (
          <p className="earnings-empty-hint">No earnings data available yet.</p>
        ) : (
          <>
            <div className="earnings-header-row">
              {isSearching ? (
                <h2>Search results for "{searchQuery.trim()}"</h2>
              ) : (
                <>
                  <h2>Week of {formatWeekLabel(selectedWeek.week_start_date)}</h2>
                  {selectedWeek.quarter_label && <span className="earnings-quarter-badge">{selectedWeek.quarter_label}</span>}
                </>
              )}
              <div className="earnings-search">
                <label htmlFor="earnings-symbol-search">Search:</label>
                <input
                  id="earnings-symbol-search"
                  type="text"
                  placeholder="Symbol or company…"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                />
              </div>
              <div className="earnings-filter">
                <label htmlFor="earnings-marketcap-filter">Market cap:</label>
                <select
                  id="earnings-marketcap-filter"
                  value={filterMode}
                  onChange={(e) => setFilterMode(e.target.value)}
                  disabled={isSearching}
                >
                  {FILTER_OPTIONS.map((opt) => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
              </div>
              <span className="earnings-count-hint">
                {isSearching
                  ? `Showing ${searchResults.length} of ${totalReleaseCount} companies across all weeks`
                  : `Showing ${filteredReleases.length} of ${selectedWeek.releases.length} companies`}
                {heldSymbols.size > 0 && ' (positions you hold are always shown)'}
              </span>
            </div>
            {isSearching ? (
              searchResults.length === 0 ? (
                <p className="earnings-empty-hint">No companies match "{searchQuery.trim()}".</p>
              ) : (
                <ul className="earnings-search-results">
                  {searchResults.map((release) => {
                    const info = heldInfo(release);
                    const openEntries = info ? Object.entries(info.open) : [];
                    const pastHolders = info ? info.past : [];
                    const isOpen = openEntries.length > 0;
                    const isPastOnly = !isOpen && pastHolders.length > 0;
                    const mcap = formatMarketCap(release.market_cap_millions);
                    const timingLabel = TIMINGS.find((t) => t.key === release.timing)?.label || release.timing;

                    return (
                      <li
                        key={release.id}
                        className={isOpen ? 'earnings-held-open' : isPastOnly ? 'earnings-held-past' : ''}
                      >
                        <div className="earnings-search-result-date">{formatFullDate(release.release_date)}</div>
                        <span className="earnings-company-name">{release.company_name}</span>
                        <span className="earnings-company-ticker">
                          {release.ticker}
                          {mcap && <span className="earnings-company-mcap"> · {mcap}</span>}
                          {' · '}{timingLabel}
                        </span>
                        {isOpen && (
                          <div className="earnings-held-info">
                            {openEntries.map(([user, entries]) => (
                              <div className="earnings-held-row" key={user}>
                                <span className="earnings-held-name">{capitalize(user)}</span>
                                <span className="earnings-held-details">
                                  {entries.map((e, i) => (
                                    <span className="earnings-held-detail" key={i}>
                                      {ASSET_LABELS[e.type] || e.type}
                                      <span className={`earnings-broker-pill earnings-broker-${e.broker}`}>
                                        {BROKER_LABELS[e.broker] || e.broker}
                                      </span>
                                    </span>
                                  ))}
                                </span>
                              </div>
                            ))}
                          </div>
                        )}
                        {isPastOnly && (
                          <div className="earnings-held-info">
                            <div className="earnings-held-row">
                              <span className="earnings-held-name">{pastHolders.map(capitalize).join(' & ')}</span>
                              <span className="earnings-held-details">
                                <span className="earnings-held-detail">
                                  past
                                  <span className="earnings-broker-pill earnings-broker-ibkr">IBKR</span>
                                </span>
                              </span>
                            </div>
                          </div>
                        )}
                      </li>
                    );
                  })}
                </ul>
              )
            ) : (
            <div className="earnings-days">
              {DAYS.map((day) => (
                <div key={day.key} className="earnings-day-section">
                  <h3 className="earnings-day-title">{day.label}</h3>
                  <div className="earnings-day-columns">
                    {TIMINGS.map((timing) => {
                      const releases = grouped[day.key][timing.key];
                      return (
                        <div key={timing.key} className={`earnings-timing-column earnings-timing-${timing.key}`}>
                          <div className="earnings-timing-label">{timing.label}</div>
                          {releases.length === 0 ? (
                            <p className="earnings-empty-slot">No releases</p>
                          ) : (
                            <ul className="earnings-company-list">
                              {releases.map((release) => {
                                const info = heldInfo(release);
                                const openEntries = info ? Object.entries(info.open) : [];
                                const pastHolders = info ? info.past : [];
                                const isOpen = openEntries.length > 0;
                                const isPastOnly = !isOpen && pastHolders.length > 0;
                                const mcap = formatMarketCap(release.market_cap_millions);

                                const titleText = isOpen
                                  ? `Open position held by ${openEntries.map(([u]) => capitalize(u)).join(' & ')}`
                                  : isPastOnly
                                    ? `Traded before by ${pastHolders.map(capitalize).join(' & ')} (IBKR), no open position now`
                                    : undefined;

                                return (
                                  <li
                                    key={release.id}
                                    className={isOpen ? 'earnings-held-open' : isPastOnly ? 'earnings-held-past' : ''}
                                    title={titleText}
                                  >
                                    <span className="earnings-company-name">{release.company_name}</span>
                                    <span className="earnings-company-ticker">
                                      {release.ticker}
                                      {mcap && <span className="earnings-company-mcap"> · {mcap}</span>}
                                    </span>
                                    {isOpen && (
                                      <div className="earnings-held-info">
                                        {openEntries.map(([user, entries]) => (
                                          <div className="earnings-held-row" key={user}>
                                            <span className="earnings-held-name">{capitalize(user)}</span>
                                            <span className="earnings-held-details">
                                              {entries.map((e, i) => (
                                                <span className="earnings-held-detail" key={i}>
                                                  {ASSET_LABELS[e.type] || e.type}
                                                  <span className={`earnings-broker-pill earnings-broker-${e.broker}`}>
                                                    {BROKER_LABELS[e.broker] || e.broker}
                                                  </span>
                                                </span>
                                              ))}
                                            </span>
                                          </div>
                                        ))}
                                      </div>
                                    )}
                                    {isPastOnly && (
                                      <div className="earnings-held-info">
                                        <div className="earnings-held-row">
                                          <span className="earnings-held-name">{pastHolders.map(capitalize).join(' & ')}</span>
                                          <span className="earnings-held-details">
                                            <span className="earnings-held-detail">
                                              past
                                              <span className="earnings-broker-pill earnings-broker-ibkr">IBKR</span>
                                            </span>
                                          </span>
                                        </div>
                                      </div>
                                    )}
                                  </li>
                                );
                              })}
                            </ul>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

export default Earnings;
